import dayjs, { Dayjs } from 'dayjs'
import * as yup from 'yup'

const isValidDate = (value: unknown): boolean => {
  return dayjs.isDayjs(value) && (value as Dayjs).isValid()
}

/**
 * Mirrors `CustomerProductForm.validator.ts`'s pattern for a picker-backed field: `yup.mixed()`
 * rejects `null` at the type-check stage before any `.test()` runs, so the field must declare
 * itself nullable before testing that it is actually present and a valid date.
 *
 * Without this, a cleared `FormikDatePicker`/`FormikTimePicker` (both write `null` via
 * `setFieldValue`) reaches `handleSubmit` as `null`, and `values.newSchedule.toDate()` throws —
 * silently, since Formik swallows the exception into a `console.warn` and Save becomes a no-op.
 *
 * The `is-future` test is what actually enforces "not in the past", NOT the panel's `minDate`.
 * `minDate` constrains the DATE picker to whole calendar days, the time picker has no minimum,
 * and `minDate` is memoized at mount — so without this test an admin could pick today and set a
 * time that has already passed. Evaluated here against `dayjs()` at submit time, so it cannot
 * go stale the way the memoized `minDate` can on a long-open page.
 */
export const rescheduleJobValidator = yup.object({
  newSchedule: yup
    .mixed()
    .nullable()
    .required('Required')
    .test('is-valid-date', 'Enter a valid date and time', (value) => {
      return isValidDate(value)
    })
    .test('is-future', 'Pick a date and time in the future', (value) => {
      if (!isValidDate(value)) {
        // Let `is-valid-date` own that failure rather than reporting two messages for one cause.
        return true
      }

      return (value as Dayjs).isAfter(dayjs())
    }),
})
